import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpPuebloClient } from '@http/http_pueblo_client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LoggingService } from '@services/logging.service';
import { PENDING_HANG_RESOURCE_NAME, makeHangResourceName } from 'pueblo-types';
import {
  EMPTY,
  catchError,
  concatMap,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectAppUser } from '../user/user-selectors';
import * as actions from './hang-actions';
import { selectHang, selectHangsLiveReloading } from './hang-selectors';

@Injectable()
export class HangEffects {
  private readonly actions$ = inject(Actions);
  private readonly puebloClient = inject(HttpPuebloClient);
  private readonly store = inject(Store);
  private readonly logger = inject(LoggingService);
  private readonly snackBar = inject(MatSnackBar);

  readonly fetchHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchHang),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] Fetching hang', resourceName);
      }),
      withLatestFrom(this.store.select(selectHangsLiveReloading)),
      mergeMap(([{ resourceName }, hangsLiveReloading]) => {
        if (hangsLiveReloading.has(resourceName)) return EMPTY;
        hangsLiveReloading.add(resourceName);
        return this.puebloClient.getHang(resourceName).pipe(
          map((hang) => actions.fetchHangSuccess({ hang })),
          tap(() => {
            this.logger.log3('[Effect] Fetched hang', resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchHangFail({ resourceName, message }));
          }),
        );
      }),
    );
  });

  readonly batchFetchHangs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.batchFetchHangs),
      tap(({ resourceNames }) => {
        this.logger.log3(`[Effect] Batch Fetching hang`, resourceNames);
      }),
      withLatestFrom(this.store.select(selectHangsLiveReloading)),
      mergeMap(([{ resourceNames }, hangsLiveReloading]) => {
        const fetchHangs = resourceNames.filter(
          (r) => !hangsLiveReloading.has(r),
        );
        if (!fetchHangs.length) return EMPTY;
        fetchHangs.forEach((h) => hangsLiveReloading.add(h));
        return this.puebloClient.batchGetHangs(fetchHangs).pipe(
          map((hangs) => actions.batchFetchHangsSuccess({ hangs })),
          tap(() => {
            this.logger.log3('[Effect] Batch Fetched hangs');
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.batchFetchHangsFail({ message }));
          }),
        );
      }),
    );
  });

  readonly createHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createHang),
      tap(({ hang }) => {
        this.logger.log3(`[Effect] Create hang `, JSON.stringify(hang));
      }),
      map(({ hang, hooks }) => {
        let hangWithId = hang;
        if (hangWithId.resourceName === PENDING_HANG_RESOURCE_NAME) {
          hangWithId = {
            ...hang,
            resourceName: makeHangResourceName(
              this.puebloClient.createHangId(),
            ),
          };
        }
        hooks?.onPending?.(hangWithId);
        return actions.createHangWithId({
          hang: hangWithId,
          hooks: hooks,
        });
      }),
    );
  });

  readonly createHangSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createHangSuccess),
      map(() => actions.fetchNextHangShortUrl()),
    );
  });

  readonly createHangWithId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createHangWithId),
      tap(({ hang }) => {
        this.logger.log3(
          `[Effect] Create hang (with id) `,
          JSON.stringify(hang),
        );
      }),
      withLatestFrom(this.store.select(selectAppUser)),
      concatMap(([{ hang, hooks: callback }, appUser]) => {
        return this.puebloClient.createHang(hang).pipe(
          tap((hang) => {
            this.logger.log3(`[Effect] Created hang `, JSON.stringify(hang));
            callback?.onSuccess?.(hang);
          }),
          map((hang) => actions.createHangSuccess({ hang })),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(
              actions.createHangFail({
                message,
                originalHang: hang,
                originalAppUser: appUser,
              }),
            );
          }),
        );
      }),
    );
  });

  readonly updateHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updateHang),
      tap(({ hang }) => {
        this.logger.log3(`[Effect] Update hang`, JSON.stringify(hang));
      }),
      concatLatestFrom(({ hang }) =>
        this.store.select(selectHang(hang.resourceName)),
      ),
      map(([{ hang, hooks }, originalHang]) => {
        if (!originalHang) {
          this.logger.error(
            `[Effect] Update hang failed: Hang not found`,
            JSON.stringify(hang),
          );
          return actions.updateHangFail({
            message: 'Hang not found',
            originalHang: undefined,
          });
        }
        hooks?.onPending?.(hang);
        return actions.updatePendingHang({
          hang,
          originalHang,
          hooks,
        });
      }),
    );
  });

  readonly updatePendingHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updatePendingHang),
      tap(({ hang }) => {
        this.logger.log3(
          `[Effect] Update hang (pending) `,
          JSON.stringify(hang),
        );
      }),
      concatMap(({ hang, originalHang, hooks }) => {
        return this.puebloClient.updateHang(hang, originalHang).pipe(
          map((hang) => actions.updateHangSuccess({ hang })),
          tap(({ hang }) => {
            this.logger.log3(
              `[Effect] Updated hang (pending) `,
              JSON.stringify(hang),
            );
            hooks?.onSuccess?.(hang);
          }),
          catchError((message) => {
            this.logger.error(message);
            hooks?.onFailure?.(message);
            return of(actions.updateHangFail({ message, originalHang }));
          }),
        );
      }),
    );
  });

  readonly deleteHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deleteHang),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete hang`, resourceName);
      }),
      concatLatestFrom(({ resourceName }) =>
        this.store.select(selectHang(resourceName)),
      ),
      map(([{ resourceName, hooks }, originalHang]) => {
        if (!originalHang) {
          this.logger.error(
            `[Effect] Delete hang failed: Hang not found`,
            resourceName,
          );
          return actions.deleteHangFail({
            message: 'Hang not found',
            originalHang: undefined,
          });
        }
        hooks?.onPending?.(resourceName);
        return actions.deletePendingHang({ resourceName, originalHang, hooks });
      }),
    );
  });

  readonly deletePendingHang$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deletePendingHang),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete hang (pending) `, resourceName);
      }),
      concatMap(({ resourceName, originalHang, hooks: callback }) => {
        return this.puebloClient.deleteHang(resourceName).pipe(
          map(() => actions.deleteHangSuccess({ resourceName })),
          tap(({ resourceName }) => {
            this.logger.log3(`[Effect] Deleted hang (pending) `, resourceName);

            this.snackBar.open('Hang deleted successfully');
            callback?.onSuccess?.(resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(actions.deleteHangFail({ message, originalHang }));
          }),
        );
      }),
    );
  });

  readonly fetchNextHangShortUrl$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchNextHangShortUrl),
      switchMap(() => {
        return this.puebloClient.getShortUrl();
      }),
      switchMap((shortUrl) => {
        return of(actions.fetchNextHangShortUrlSuccess({ shortUrl }));
      }),
      catchError((message) => {
        this.logger.error(message);
        return of(actions.fetchNextHangShortUrlFail({ message }));
      }),
    );
  });

  readonly displayErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          actions.fetchHangFail,
          actions.batchFetchHangsFail,
          actions.updateHangFail,
          actions.createHangFail,
          actions.fetchNextHangShortUrlFail,
        ),
        mergeMap(({ message }) => {
          // TODO: Return better/human-readable error messages.
          this.snackBar.open(message);
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );
}
